import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { StructureObservable } from '../../core/service/observable.service';
import { StructureEvent, ProcessingStep } from '../../core/models/websocket/structure';

export type JobPhase = 'analysis' | 'extraction' | 'structuring' | 'validation';
export type MilestoneStatus = 'pending' | 'in_progress' | 'completed';

export interface Milestone {
  id: string;
  label: string;
  status: MilestoneStatus;
  visible: boolean;
}

export interface JobStep {
  id: JobPhase;
  title: string;
  description: string;
  status: MilestoneStatus;
  milestones: Milestone[];
  expanded: boolean;
}

interface JobState {
  steps: JobStep[];
  currentPhase: JobPhase;
  overallProgress: number;
  startTime: number;
  phaseStartTime: number;
  estimatedTotalSeconds: number;
  hasStartedProcessing?: boolean;
  completedBackendSteps?: ProcessingStep[];
  validationStartTime?: number;
  isFinished?: boolean;
  pendingTargetPhase?: JobPhase | null;
}

@Component({
  selector: 'app-job-management',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './job-management.component.html',
  styleUrls: ['./job-management.component.scss'],
})
export class JobManagementComponent implements OnInit, OnDestroy, OnChanges {
  private static readonly STORAGE_KEY = 'job_management_states';

  // Minimum duration each phase must be shown (in ms), even if backend completes instantly
  private static readonly MIN_PHASE_DURATION_MS: Record<JobPhase, number> = {
    'analysis': 1500,
    'extraction': 2000,
    'structuring': 2000,
    'validation': 2000,
  };

  // Maps backend ProcessingStep to UI JobPhase completion
  private static readonly STEP_TO_PHASE: Partial<Record<ProcessingStep, JobPhase>> = {
    'initialization': 'analysis',
    'extraction': 'extraction',
    'conversion': 'structuring',
  };

  // Phase progress boundaries
  private static readonly PHASE_RANGES: Record<JobPhase, { start: number; end: number }> = {
    'analysis': { start: 0, end: 10 },
    'extraction': { start: 10, end: 40 },
    'structuring': { start: 40, end: 85 },
    'validation': { start: 85, end: 100 },
  };

  // Estimated duration split (as fraction of total)
  private static readonly PHASE_DURATION_FRACTION: Record<JobPhase, number> = {
    'analysis': 0.05,
    'extraction': 0.35,
    'structuring': 0.55,
    'validation': 0.05,
  };

  @Input() tableId: string = '';
  @Input() fileId: string = '';
  @Input() fileResourceKey: string = '';
  @Input() fileProcessId: string = '';
  @Input() tableName: string = '';
  @Input() fileName: string = '';
  @Input() isStructuring: boolean = false;
  @Input() fileStatus: string | undefined = undefined;
  @Input() processingProgress: {
    current: number;
    total: number;
    processedFiles: string[];
    failedFiles: string[];
  } = { current: 0, total: 0, processedFiles: [], failedFiles: [] };
  @Input() estimatedTotalSeconds: number = 120;

  @Output() cancel = new EventEmitter<void>();
  @Output() runInBackground = new EventEmitter<MouseEvent>();
  @Output() completed = new EventEmitter<void>();

  steps: JobStep[] = [];
  currentPhase: JobPhase = 'analysis';
  overallProgress: number = 0;
  isQueued: boolean = false;

  private structureSubscription?: Subscription;
  private progressInterval?: ReturnType<typeof setInterval>;
  private phaseStartTime: number = 0;
  private startTime: number = 0;
  private lastStateKey: string = '';
  
  private hasStartedProcessing: boolean = false;
  private completedBackendSteps: Set<ProcessingStep> = new Set();
  private validationStartTime: number = 0;
  private isFinished: boolean = false;
  private pendingTargetPhase: JobPhase | null = null; // Phase to advance to after min duration

  readonly stepMarkers = [
    { position: '10%', afterPhase: 0 },
    { position: '40%', afterPhase: 1 },
    { position: '85%', afterPhase: 2 },
  ];

  private get stateKey(): string {
    return `${this.tableId}:${this.fileId}`;
  }

  private isEventForCurrentFile(eventFileId: string | undefined): boolean {
    if (!eventFileId) {
      return true;
    }
    return (
      eventFileId === this.fileId ||
      eventFileId === this.fileResourceKey ||
      eventFileId === this.fileProcessId ||
      Boolean(this.fileId && eventFileId.includes(this.fileId)) ||
      Boolean(this.fileResourceKey && eventFileId.includes(this.fileResourceKey)) ||
      Boolean(this.fileProcessId && eventFileId === this.fileProcessId)
    );
  }

  private readonly analysisMilestones: Milestone[] = [
    { id: 'doc_type', label: 'Identifying document type', status: 'pending', visible: false },
    { id: 'pages', label: 'Pages detected', status: 'pending', visible: false },
    { id: 'sections', label: 'Sections identified', status: 'pending', visible: false },
    { id: 'layout', label: 'Layout regions detected', status: 'pending', visible: false },
    { id: 'structure', label: 'Structure indexed', status: 'pending', visible: false },
  ];

  private readonly extractionMilestones: Milestone[] = [
    { id: 'text_blocks', label: 'Text blocks extracted', status: 'pending', visible: false },
    { id: 'headings', label: 'Headings normalized', status: 'pending', visible: false },
    { id: 'tables_detected', label: 'Tables and lists detected', status: 'pending', visible: false },
    { id: 'paragraphs', label: 'Paragraph blocks extracted', status: 'pending', visible: false },
    { id: 'markdown_fmt', label: 'Markdown formatting applied', status: 'pending', visible: false },
    { id: 'consistency', label: 'Consistency check passed', status: 'pending', visible: false },
  ];

  private readonly structuringMilestones: Milestone[] = [
    { id: 'markdown_parse', label: 'Markdown parsed into logical blocks', status: 'pending', visible: false },
    { id: 'field_names', label: 'Field names standardized', status: 'pending', visible: false },
    { id: 'data_types', label: 'Data types inferred', status: 'pending', visible: false },
    { id: 'records_id', label: 'Records identified', status: 'pending', visible: false },
    { id: 'cols_aligned', label: 'Columns aligned across sections', status: 'pending', visible: false },
    { id: 'consistency_check', label: 'Consistency checks completed', status: 'pending', visible: false },
  ];

  private readonly validationMilestones: Milestone[] = [
    { id: 'cross_check', label: 'Cross checking totals and units', status: 'pending', visible: false },
    { id: 'cleaning', label: 'Cleaning messy fields', status: 'pending', visible: false },
    { id: 'flagging', label: 'Flagging anomalies', status: 'pending', visible: false },
    { id: 'dataset_assembled', label: 'Final dataset assembled', status: 'pending', visible: false },
    { id: 'output_finalized', label: 'Structured output finalized', status: 'pending', visible: false },
  ];

  constructor(
    private structureObservable: StructureObservable,
    private cdRef: ChangeDetectorRef
  ) {
    this.initializeSteps();
  }

  ngOnInit(): void {
    this.subscribeToStructureUpdates();
    this.handleFileStatusChange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['tableId'] || changes['fileId']) && this.tableId && this.fileId) {
      const currentKey = this.stateKey;
      if (this.lastStateKey && this.lastStateKey !== currentKey) {
        this.stopProgressSimulation();
        this.startTime = 0;
        this.phaseStartTime = 0;
        this.overallProgress = 0;
        this.currentPhase = 'analysis';
        this.hasStartedProcessing = false;
        this.completedBackendSteps.clear();
        this.validationStartTime = 0;
        this.isFinished = false;
        this.pendingTargetPhase = null;
        this.initializeSteps();
      }
      this.lastStateKey = currentKey;
      this.handleFileStatusChange();
    }

    if (changes['fileStatus']) {
      this.handleFileStatusChange();
    }

    if (changes['isStructuring']) {
      if (!this.isStructuring) {
        this.stopProgressSimulation();
        this.removeStateFromStorage(this.stateKey);
      } else {
        this.handleFileStatusChange();
      }
    }

    if (changes['processingProgress'] && this.processingProgress) {
      this.checkIfCurrentFileCompleted();
    }
  }

  private handleFileStatusChange(): void {
    if (!this.isStructuring) {
      this.stopProgressSimulation();
      this.hasStartedProcessing = false;
      this.initializeSteps();
      return;
    }

    const savedState = this.loadStateFromStorage(this.stateKey);
    if (savedState) {
      if (savedState.hasStartedProcessing || (savedState.overallProgress > 0 && savedState.overallProgress < 100)) {
        this.hasStartedProcessing = true;
      }
      if (savedState.completedBackendSteps) {
        this.completedBackendSteps = new Set(savedState.completedBackendSteps);
      }
      if (savedState.validationStartTime) {
        this.validationStartTime = savedState.validationStartTime;
      }
      if (savedState.isFinished) {
        this.isFinished = savedState.isFinished;
      }
      if (savedState.pendingTargetPhase !== undefined) {
        this.pendingTargetPhase = savedState.pendingTargetPhase;
      }
    }

    switch (this.fileStatus) {
      case 'finished':
        this.isFinished = true;
        this.handleFinishedEvent();
        break;
        
      case 'failed':
        this.stopProgressSimulation();
        this.showFailedState();
        break;
        
      case 'queued':
      case 'processing':
        this.hasStartedProcessing = true;
        this.startOrResumeProgress();
        break;
        
      default:
        if (this.hasStartedProcessing) {
          this.startOrResumeProgress();
        } else {
          this.showWaitingState();
        }
        break;
    }
  }

  private showWaitingState(): void {
    this.initializeSteps();
    this.steps[0].status = 'pending';
    this.steps[0].description = 'Waiting to start...';
    this.steps[0].expanded = true;
    this.overallProgress = 0;
  }

  private getEstimatedPhaseDurationMs(phase: JobPhase): number {
    const fraction = JobManagementComponent.PHASE_DURATION_FRACTION[phase];
    return this.estimatedTotalSeconds * 1000 * fraction;
  }

  /** Calculates asymptotic progress within a phase that approaches but never reaches 1 */
  private calculateAsymptoticProgress(elapsed: number, estimatedDuration: number): number {
    // Using formula: progress = elapsed / (elapsed + halfLife)
    // halfLife is set so that at estimatedDuration, progress is ~0.9
    const halfLife = estimatedDuration / 9; // At t=estimated, progress = 9/(9+1) = 0.9
    return elapsed / (elapsed + halfLife);
  }

  private startProgressSimulation(): void {
    this.stopProgressSimulation();
    this.isQueued = false;
    
    if (!this.startTime) {
      this.startTime = Date.now();
      this.phaseStartTime = Date.now();
    }
    
    const activePhase: JobPhase = this.currentPhase || 'analysis';
    const activeStep = this.steps.find((s) => s.id === activePhase) || this.steps[0];
    if (activeStep) {
      this.steps.forEach((s) => {
        s.expanded = s.id === activeStep.id;
      });
      if (this.overallProgress < 100 && activeStep.status !== 'completed') {
        activeStep.status = 'in_progress';
      }
      if (!activeStep.description) {
        activeStep.description =
          activeStep.id === 'analysis'
            ? 'Analyzing your document...'
            : activeStep.id === 'extraction'
              ? 'Extracting data...'
              : activeStep.id === 'structuring'
                ? 'Structuring data...'
                : 'Final quality check...';
      }
    }

    this.progressInterval = setInterval(() => {
      this.simulateAsymptoticProgress();
      this.saveStateToStorage(this.stateKey);
      this.cdRef.detectChanges();
    }, 200);
  }

  private stopProgressSimulation(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = undefined;
    }
  }

  private simulateAsymptoticProgress(): void {
    if (this.overallProgress >= 100) {
      this.stopProgressSimulation();
      console.log('simulateAsymptoticProgress.stopProgressSimulation');
      return;
    }
    
    if (this.fileStatus === 'failed') {
      this.stopProgressSimulation();
      console.log('simulateAsymptoticProgress.stopProgressSimulation');
      return;
    }

    const now = Date.now();
    const phaseElapsed = now - this.phaseStartTime;
    const minPhaseDuration = JobManagementComponent.MIN_PHASE_DURATION_MS[this.currentPhase];
    const hasMetMinDuration = phaseElapsed >= minPhaseDuration;
    
    // Check if we should advance to a pending target phase
    if (this.pendingTargetPhase && hasMetMinDuration) {
      this.advanceToPendingPhase();
      return; // Let next tick handle the new phase
    }
    
    const estimatedPhaseDuration = this.getEstimatedPhaseDurationMs(this.currentPhase);
    
    // Calculate asymptotic progress within the current phase
    // If we have a pending advancement, accelerate towards completion
    let phaseProgressRatio: number;
    if (this.pendingTargetPhase) {
      // Accelerate: use minimum duration as the "estimated" to speed up
      phaseProgressRatio = this.calculateAsymptoticProgress(phaseElapsed, minPhaseDuration * 0.8);
    } else {
      phaseProgressRatio = this.calculateAsymptoticProgress(phaseElapsed, estimatedPhaseDuration);
    }
    
    // Update milestones based on progress
    this.updateMilestonesForProgress(phaseProgressRatio);
    
    // Calculate overall progress
    this.overallProgress = this.calculateOverallProgress(phaseProgressRatio);
    
    // Handle validation phase completion (with minimum duration)
    // Only complete if validationStartTime is actually set (> 0)
    if (this.currentPhase === 'validation' && this.isFinished && this.validationStartTime > 0) {
      const validationElapsed = now - this.validationStartTime;
      const validationMinDuration = JobManagementComponent.MIN_PHASE_DURATION_MS['validation'];
      if (validationElapsed >= validationMinDuration) {
        console.log('validationElapsed >= validationMinDuration, completing all steps');
        this.completeAllSteps();
      }
    }
  }

  /** Advances through phases to reach the pending target phase */
  private advanceToPendingPhase(): void {
    if (!this.pendingTargetPhase) return;
    
    const phaseOrder: JobPhase[] = ['analysis', 'extraction', 'structuring', 'validation'];
    const currentIndex = phaseOrder.indexOf(this.currentPhase);
    const targetIndex = phaseOrder.indexOf(this.pendingTargetPhase);
    
    if (currentIndex < targetIndex) {
      // Complete current phase and advance to next
      this.completeStep(this.currentPhase);
      const nextPhase = phaseOrder[currentIndex + 1];
      console.log('advanceToPendingPhase.nextPhase', nextPhase);
      this.advanceToPhase(nextPhase);
      
      // If we've reached the target, clear the pending state
      if (nextPhase === this.pendingTargetPhase) {
        this.pendingTargetPhase = null;
      }
      
      // If advancing to validation, record the start time
      if (nextPhase === 'validation') {
        this.validationStartTime = Date.now();
      }
    } else {
      // Already at or past target
      this.pendingTargetPhase = null;
    }
    
    this.saveStateToStorage(this.stateKey);
  }

  private updateMilestonesForProgress(progressRatio: number): void {
    const currentStep = this.steps.find((s) => s.id === this.currentPhase);
    if (!currentStep) return;

    const totalMilestones = currentStep.milestones.length;
    // Leave some buffer - don't complete all milestones until phase is actually done
    const maxRevealRatio = 0.95;
    const effectiveRatio = Math.min(progressRatio, maxRevealRatio);
    const milestonesToReveal = Math.min(
      Math.floor(effectiveRatio * (totalMilestones + 1)) + 1,
      totalMilestones
    );

    currentStep.milestones.forEach((m, index) => {
      if (index < milestonesToReveal - 1) {
        m.visible = true;
        m.status = 'completed';
      } else if (index === milestonesToReveal - 1) {
        m.visible = true;
        m.status = 'in_progress';
      } else {
        m.visible = false;
        m.status = 'pending';
      }
    });
  }

  private calculateOverallProgress(phaseProgressRatio: number): number {
    const phaseRanges = JobManagementComponent.PHASE_RANGES;
    const phaseOrder: JobPhase[] = ['analysis', 'extraction', 'structuring', 'validation'];
    const currentIndex = phaseOrder.indexOf(this.currentPhase);
    
    // Sum up completed phases
    let progress = 0;
    for (let i = 0; i < currentIndex; i++) {
      const range = phaseRanges[phaseOrder[i]];
      progress = range.end;
    }
    
    // Add current phase progress (asymptotic, never reaches full boundary)
    const currentRange = phaseRanges[this.currentPhase];
    const phaseWidth = currentRange.end - currentRange.start;
    
    // Leave a 1% buffer before boundary for non-validation phases
    const buffer = this.currentPhase === 'validation' ? 0 : 1;
    const maxPhaseProgress = phaseWidth - buffer;
    const phaseContribution = maxPhaseProgress * phaseProgressRatio;
    progress = currentRange.start + phaseContribution;
    
    return Math.min(Math.floor(progress), 99);
  }

  /** Called when a backend processing step is completed */
  public onBackendStepCompleted(step: ProcessingStep): void {
    if (this.completedBackendSteps.has(step)) {
      return; // Already processed this step
    }
    
    this.completedBackendSteps.add(step);
    
    // Map backend step to UI phase
    const completedPhase = JobManagementComponent.STEP_TO_PHASE[step];
    if (!completedPhase) {
      return; // Unmapped step (e.g., 'recommendation', 'validation')
    }
    
    // Determine the target phase (next phase after the completed one)
    const phaseOrder: JobPhase[] = ['analysis', 'extraction', 'structuring', 'validation'];
    const completedPhaseIndex = phaseOrder.indexOf(completedPhase);
    const nextPhaseIndex = completedPhaseIndex + 1;
    
    if (nextPhaseIndex < phaseOrder.length) {
      const targetPhase = phaseOrder[nextPhaseIndex];
      const currentPhaseIndex = phaseOrder.indexOf(this.currentPhase);
      
      // Only set target if it's ahead of current phase
      if (nextPhaseIndex > currentPhaseIndex) {
        // Set or update pending target to the furthest phase we should reach
        const currentTargetIndex = this.pendingTargetPhase 
          ? phaseOrder.indexOf(this.pendingTargetPhase) 
          : -1;
        
        if (nextPhaseIndex > currentTargetIndex) {
          this.pendingTargetPhase = targetPhase;
        }
      }
    }
    
    this.saveStateToStorage(this.stateKey);
    this.cdRef.detectChanges();
  }

  /** Called when the job is finished (from fileStatus or event) */
  private handleFinishedEvent(): void {
    console.log('handleFinishedEvent');
    this.isFinished = true;
    
    // Set validation as the target phase (will advance through phases respecting min durations)
    if (this.currentPhase !== 'validation') {
      this.pendingTargetPhase = 'validation';
    }
    
    // If already in validation and it hasn't started timing yet, start it now
    if (this.currentPhase === 'validation' && !this.validationStartTime) {
      console.log('validationStartTime not set, setting it now');
      this.validationStartTime = Date.now();
    }
    
    // Ensure simulation is running for the phase advancement
    if (!this.progressInterval) {
      this.startProgressSimulation();
    }
    
    this.saveStateToStorage(this.stateKey);
    this.cdRef.detectChanges();
  }

  /** Process completed backend steps from event */
  private processCompletedSteps(stepsCompleted: ProcessingStep[]): void {
    for (const step of stepsCompleted) {
      if (!this.completedBackendSteps.has(step)) {
        this.onBackendStepCompleted(step);
      }
    }
  }

  private startOrResumeProgress(): void {
    const savedState = this.loadStateFromStorage(this.stateKey);
    
    if (savedState) {
      if (savedState.overallProgress >= 100 || savedState.isFinished) {
        this.completeAllSteps();
        return;
      }
      
      if (savedState.overallProgress === -1) {
        this.showFailedState();
        return;
      }
      
      this.restoreFromState(savedState);
    } else if (this.overallProgress === 0 || this.currentPhase === 'analysis' && this.steps[0].status === 'pending') {
      this.resetSteps();
    }
    
    if (!this.progressInterval) {
      this.startProgressSimulation();
    }
  }

  private showFailedState(): void {
    this.initializeSteps();
    this.steps.forEach(step => {
      step.status = 'pending';
    });
    this.steps[0].status = 'in_progress';
    this.steps[0].description = 'Processing failed';
    this.overallProgress = 0;
  }

  ngOnDestroy(): void {
    if (this.stateKey && this.isStructuring && this.fileStatus === 'processing') {
      this.saveStateToStorage(this.stateKey);
    }
    this.structureSubscription?.unsubscribe();
    this.stopProgressSimulation();
  }

  private loadStateFromStorage(stateKey: string): JobState | null {
    try {
      const stored = localStorage.getItem(JobManagementComponent.STORAGE_KEY);
      if (stored) {
        const allStates = JSON.parse(stored) as Record<string, JobState>;
        return allStates[stateKey] || null;
      }
    } catch (e) {
      console.error('Failed to load job state from storage', e);
    }
    return null;
  }

  private saveStateToStorage(stateKey: string): void {
    try {
      const stored = localStorage.getItem(JobManagementComponent.STORAGE_KEY);
      const allStates = stored ? JSON.parse(stored) : {};
      allStates[stateKey] = {
        steps: JSON.parse(JSON.stringify(this.steps)),
        currentPhase: this.currentPhase,
        overallProgress: this.overallProgress,
        startTime: this.startTime,
        phaseStartTime: this.phaseStartTime,
        estimatedTotalSeconds: this.estimatedTotalSeconds,
        hasStartedProcessing: this.hasStartedProcessing,
        completedBackendSteps: Array.from(this.completedBackendSteps),
        validationStartTime: this.validationStartTime,
        isFinished: this.isFinished,
        pendingTargetPhase: this.pendingTargetPhase,
      };
      localStorage.setItem(JobManagementComponent.STORAGE_KEY, JSON.stringify(allStates));
    } catch (e) {
      console.error('Failed to save job state to storage', e);
    }
  }

  private removeStateFromStorage(stateKey: string): void {
    try {
      const stored = localStorage.getItem(JobManagementComponent.STORAGE_KEY);
      if (stored) {
        const allStates = JSON.parse(stored);
        delete allStates[stateKey];
        localStorage.setItem(JobManagementComponent.STORAGE_KEY, JSON.stringify(allStates));
      }
    } catch (e) {
      console.error('Failed to remove job state from storage', e);
    }
  }

  private restoreFromState(state: JobState): void {
    this.steps = JSON.parse(JSON.stringify(state.steps));
    this.currentPhase = state.currentPhase;
    this.overallProgress = state.overallProgress;
    this.startTime = state.startTime;
    this.hasStartedProcessing = state.hasStartedProcessing ?? (state.overallProgress > 0);
    this.completedBackendSteps = new Set(state.completedBackendSteps ?? []);
    this.validationStartTime = state.validationStartTime ?? 0;
    this.isFinished = state.isFinished ?? false;
    this.pendingTargetPhase = state.pendingTargetPhase ?? null;
    
    // Restore phaseStartTime or use current time if not available
    this.phaseStartTime = state.phaseStartTime || Date.now();
  }

  private initializeSteps(): void {
    this.isQueued = false;
    this.steps = [
      {
        id: 'analysis',
        title: 'Analysis',
        description: 'Analyzing your document...',
        status: 'pending',
        milestones: this.deepCopyMilestones(this.analysisMilestones),
        expanded: true,
      },
      {
        id: 'extraction',
        title: 'Extraction',
        description: 'Extracting data...',
        status: 'pending',
        milestones: this.deepCopyMilestones(this.extractionMilestones),
        expanded: false,
      },
      {
        id: 'structuring',
        title: 'Structuring',
        description: 'Structuring data...',
        status: 'pending',
        milestones: this.deepCopyMilestones(this.structuringMilestones),
        expanded: false,
      },
      {
        id: 'validation',
        title: 'Validation',
        description: 'Final quality check...',
        status: 'pending',
        milestones: this.deepCopyMilestones(this.validationMilestones),
        expanded: false,
      },
    ];
  }

  private resetSteps(): void {
    this.initializeSteps();
    this.currentPhase = 'analysis';
    this.overallProgress = 0;
    this.phaseStartTime = Date.now();
    this.startTime = Date.now();
    this.completedBackendSteps.clear();
    this.validationStartTime = 0;
    this.isFinished = false;
    this.pendingTargetPhase = null;

    const firstStep = this.steps[0];
    firstStep.status = 'in_progress';
    firstStep.expanded = true;
  }

  private deepCopyMilestones(milestones: Milestone[]): Milestone[] {
    return milestones.map((m) => ({ ...m }));
  }

  private subscribeToStructureUpdates(): void {
    this.structureSubscription = this.structureObservable
      .updates()
      .subscribe((event: StructureEvent) => {
        if (!this.tableId) return;
        const matchesTable = !event.tableId || event.tableId === this.tableId;
        if (matchesTable) {
          this.handleStructureEvent(event);
        }
      });
  }

  private handleStructureEvent(event: StructureEvent): void {
    const isForCurrentFile = this.isEventForCurrentFile(event.fileId);
    const isTableLevel = !event.fileId;
    
    if (!(isForCurrentFile || isTableLevel)) {
      return;
    }

    // Process completed backend steps from processingStatus
    if (event.processingStatus?.steps_completed) {
      this.processCompletedSteps(event.processingStatus.steps_completed);
    }
    
    switch (event.status) {
      case 'all-start':
        break;
        
      case 'processing':
        if (!this.hasStartedProcessing) {
          this.hasStartedProcessing = true;
          this.startOrResumeProgress();
        }
        break;
        
      case 'segments':
        // Legacy: treat segments as extraction complete
        if (!this.completedBackendSteps.has('extraction')) {
          this.onBackendStepCompleted('extraction');
        }
        break;
        
      case 'finished':
      case 'table':
        this.handleFinishedEvent();
        break;
        
      case 'all-done':
        this.completeAllSteps();
        break;
        
      case 'failed':
      case 'cancelled':
        this.stopProgressSimulation();
        this.showFailedState();
        break;
        
      case 'queued':
        break;
    }

    this.cdRef.detectChanges();
  }

  private advanceToPhase(phase: JobPhase): void {
    const phaseOrder: JobPhase[] = ['analysis', 'extraction', 'structuring', 'validation'];
    const currentIndex = phaseOrder.indexOf(this.currentPhase);
    const newIndex = phaseOrder.indexOf(phase);

    for (let i = 0; i <= currentIndex; i++) {
      if (i < newIndex) {
        this.completeStep(phaseOrder[i]);
      }
    }

    this.currentPhase = phase;
    const step = this.steps.find((s) => s.id === phase);
    if (step) {
      step.status = 'in_progress';
      step.expanded = true;
      this.steps.forEach((s) => {
        if (s.id !== phase) {
          s.expanded = false;
        }
      });
    }

    this.phaseStartTime = Date.now();
  }

  private completeStep(phase: JobPhase): void {
    const step = this.steps.find((s) => s.id === phase);
    if (step) {
      step.status = 'completed';
      step.milestones.forEach((m) => {
        m.status = 'completed';
        m.visible = true;
      });
      step.expanded = false;
    }
  }

  private completeAllSteps(): void {
    console.log('completeAllSteps');
    if (this.overallProgress >= 100) {
      console.log('overallProgress >= 100');
      return;
    }
    
    // Ensure validation phase has had its minimum duration
    const validationMinDuration = JobManagementComponent.MIN_PHASE_DURATION_MS['validation'];
    const now = Date.now();
    
    // If we're not in validation yet, or validation hasn't started timing, defer to handleFinishedEvent
    if (this.currentPhase !== 'validation' || this.validationStartTime === 0) {
      console.log('completeAllSteps: deferring - not in validation or validationStartTime not set');
      this.handleFinishedEvent();
      return;
    }
    
    // If validation minimum duration hasn't been met, defer completion
    const validationElapsed = now - this.validationStartTime;
    if (validationElapsed < validationMinDuration) {
      console.log('completeAllSteps: deferring - validation min duration not met', validationElapsed, '<', validationMinDuration);
      this.isFinished = true;
      // Ensure simulation is running to complete after min duration
      if (!this.progressInterval) {
        this.startProgressSimulation();
      }
      return;
    }
    
    console.log('completeAllSteps: completing now');
    this.stopProgressSimulation();
    
    this.overallProgress = 100;
    this.isQueued = false;
    this.currentPhase = 'validation';
    this.isFinished = true;
    
    this.steps.forEach((step) => {
      step.status = 'completed';
      step.milestones.forEach((m) => {
        m.status = 'completed';
        m.visible = true;
      });
      step.expanded = false;
    });
    
    this.removeStateFromStorage(this.stateKey);
    this.cdRef.detectChanges();
    
    // Notify parent that the job is visually complete
    this.completed.emit();
    console.log('completed.emit');
  }

  private checkIfCurrentFileCompleted(): void {
    const processedFiles = this.processingProgress.processedFiles || [];
    const failedFiles = this.processingProgress.failedFiles || [];
    
    const isFileProcessed = this.isFileInList(processedFiles);
    const isFileFailed = this.isFileInList(failedFiles);
    
    if (isFileProcessed && !this.isFinished) {
      // Use handleFinishedEvent to allow validation animation to play
      this.handleFinishedEvent();
    } else if (isFileFailed) {
      this.stopProgressSimulation();
      this.showFailedState();
    }
  }

  private isFileInList(fileList: string[]): boolean {
    if (!fileList || fileList.length === 0) {
      return false;
    }
    
    for (const listFileId of fileList) {
      if (listFileId === this.fileId) {
        return true;
      }
      if (listFileId === this.fileResourceKey) {
        return true;
      }
      if (listFileId === this.fileProcessId) {
        return true;
      }
      if (this.fileId && listFileId.includes(this.fileId)) {
        return true;
      }
      if (this.fileResourceKey && listFileId.includes(this.fileResourceKey)) {
        return true;
      }
    }
    
    return false;
  }

  toggleStep(step: JobStep): void {
    step.expanded = !step.expanded;
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onRunInBackground(event: MouseEvent): void {
    if (!event?.isTrusted) return;
    event.preventDefault();
    event.stopPropagation();
    this.runInBackground.emit(event);
  }

  getStepIcon(step: JobStep): string {
    switch (step.status) {
      case 'completed':
        return '✓';
      case 'in_progress':
        return '';
      default:
        return '';
    }
  }

  getMilestoneIcon(milestone: Milestone): string {
    switch (milestone.status) {
      case 'completed':
        return '✓';
      case 'in_progress':
        return '→';
      default:
        return '○';
    }
  }

  getProgressBarStyle(): { [key: string]: string } {
    return {
      width: `${this.overallProgress}%`,
    };
  }

  isMarkerActive(markerIndex: number): boolean {
    const phaseOrder: JobPhase[] = ['analysis', 'extraction', 'structuring', 'validation'];
    const currentPhaseIndex = phaseOrder.indexOf(this.currentPhase);
    return currentPhaseIndex === this.stepMarkers[markerIndex].afterPhase + 1;
  }

  isMarkerCompleted(markerIndex: number): boolean {
    const phaseOrder: JobPhase[] = ['analysis', 'extraction', 'structuring', 'validation'];
    const currentPhaseIndex = phaseOrder.indexOf(this.currentPhase);
    return currentPhaseIndex > this.stepMarkers[markerIndex].afterPhase + 1;
  }

  getCompletedMilestonesCount(step: JobStep): number {
    return step.milestones.filter((m) => m.visible && m.status === 'completed').length;
  }

  getVisibleMilestonesCount(step: JobStep): number {
    return step.milestones.filter((m) => m.visible).length;
  }

  get displayTitle(): string {
    const displayName = this.fileName || this.tableName;
    return displayName
      ? `Structuring ${displayName}...`
      : 'Structuring your documents...';
  }

  get timeEstimateDisplay(): string {
    const estimatedSeconds = this.estimatedTotalSeconds;
    const estimatedMinutes = estimatedSeconds / 60;
    
    let minMinutes = Math.ceil(estimatedMinutes * 0.95);
    let maxMinutes = Math.ceil(estimatedMinutes * 1.05);
    
    if (minMinutes < 1) minMinutes = 1;
    if (maxMinutes < 1) maxMinutes = 1;
    
    if (minMinutes === maxMinutes) {
      maxMinutes += 1;
    }
    
    return `Usually ${minMinutes}-${maxMinutes} min`;
  }
}
